'use client';

import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { motion, AnimatePresence, useSpring, useTransform, useInView, useMotionValue } from 'framer-motion';
import RisingParticlesScene from './RisingParticlesScene';

const AnimatedNumber = ({ value, suffix = '' }: { value: number, suffix?: string }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });
    const motionValue = useMotionValue(0);
    const springValue = useSpring(motionValue, { stiffness: 40, damping: 20, duration: 2 });
    const displayValue = useTransform(springValue, (latest) => Math.floor(latest) + suffix);

    useEffect(() => {
        if (inView) {
            motionValue.set(value);
        }
    }, [inView, value, motionValue]);

    return <motion.span ref={ref}>{displayValue}</motion.span>;
};

interface ExpertiseShowcaseProps {
    onBookConsultation?: () => void;
}

const ExpertiseShowcase = ({ onBookConsultation }: ExpertiseShowcaseProps) => {
    const [openSection, setOpenSection] = useState<string | null>(null);

    const toggleSection = (id: string) => {
        setOpenSection(openSection === id ? null : id);
    };

    const expertiseAreas = [
        {
            id: '01',
            title: 'Google Business Profile Mastery',
            subtitle: 'Verification & Optimization',
            details: [
                'Profile verification and setup optimization',
                'Strategic keyword integration in business descriptions',
                'Review generation and management systems',
                'Post scheduling for maximum engagement',
                'Q&A optimization and monitoring',
                'Photo and video content strategy',
                'Insights analysis and reporting'
            ]
        },
        {
            id: '02',
            title: 'Advanced SEO Strategies',
            subtitle: 'Technical Foundation & Authority',
            details: [
                'Technical SEO audits and implementation',
                'On-page optimization and content strategy',
                'Local SEO and citation building',
                'Link building and authority development',
                'Schema markup implementation',
                'Core Web Vitals optimization',
                'Mobile-first indexing strategies'
            ]
        },
        {
            id: '03',
            title: 'Reputation Management',
            subtitle: 'Brand Defense & Analysis',
            details: [
                'Multi-platform review monitoring',
                'Professional response crafting',
                'Negative review mitigation strategies',
                'Rating improvement campaigns',
                'Brand sentiment analysis',
                'Crisis management protocols',
                'Customer feedback integration'
            ]
        }
    ];

    return (
        <section className="bg-transparent py-32 overflow-hidden w-full relative">

            {/* Background Particles */}
            <RisingParticlesScene />

            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">

                {/* Header Section - Massive Tyopgraphy */}
                <div className="grid lg:grid-cols-12 gap-12 mb-24 border-b border-black pb-12">
                    <div className="lg:col-span-8">
                        <motion.h2
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="text-7xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-slate-900 leading-[0.9]"
                        >
                            INSIDER<br />
                            EXPERTISE.
                        </motion.h2>
                    </div>
                    <div className="lg:col-span-4 flex flex-col justify-end">
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 0.8 }}
                            className="text-xl md:text-2xl font-medium text-slate-500 leading-tight"
                        >
                            With years of hands-on experience in Google Business Profile optimization and strategic SEO management, our team brings insider knowledge that delivers measurable results for brands across industries.
                        </motion.p>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-12 gap-0 border-t border-slate-200">

                    {/* Left: Expertise List */}
                    <div className="lg:col-span-8 border-r border-slate-200">
                        {expertiseAreas.map((area, index) => {
                            const isOpen = openSection === area.id;

                            return (
                                <motion.div
                                    key={area.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    onClick={() => toggleSection(area.id)}
                                    className={`group relative border-b border-slate-200 p-8 lg:p-12 transition-all duration-500 cursor-pointer overflow-hidden ${isOpen ? 'bg-slate-50/80 backdrop-blur-sm' : 'hover:bg-slate-50/50 hover:backdrop-blur-sm'}`}
                                >
                                    {/* Header Row */}
                                    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between mb-2">
                                        <div className="flex items-baseline space-x-6 z-10 relative">
                                            <span className={`text-sm font-mono font-bold transition-colors duration-300 ${isOpen ? 'text-indigo-600' : 'text-slate-400 group-hover:text-indigo-600'}`}>
                                                {area.id}
                                            </span>
                                            <h3 className={`text-3xl lg:text-4xl font-bold tracking-tight transition-all duration-300 ${isOpen ? 'text-indigo-900 translate-x-2' : 'text-slate-900 group-hover:translate-x-2'}`}>
                                                {area.title}
                                            </h3>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <span className="mt-2 md:mt-0 text-lg text-slate-400 font-medium hidden sm:inline-block">
                                                {area.subtitle}
                                            </span>
                                            <div className={`transition-transform duration-500 ${isOpen ? 'rotate-180 text-indigo-600' : 'rotate-0 text-slate-300 group-hover:text-indigo-600'}`}>
                                                <Icon name="ChevronDownIcon" size={24} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expandable Content */}
                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.4, ease: "easeInOut" }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pt-8 pl-0 md:pl-12">
                                                    <div className="w-12 h-0.5 bg-indigo-600 mb-6" />
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                                        {area.details.map((detail, i) => (
                                                            <motion.div
                                                                key={i}
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: i * 0.05 }}
                                                                className="flex items-start space-x-3 mb-2"
                                                            >
                                                                <Icon name="CheckCircleIcon" size={18} className="text-indigo-600 mt-1 flex-shrink-0" />
                                                                <span className="text-base font-medium text-slate-600 leading-snug hover:text-slate-900 transition-colors">
                                                                    {detail}
                                                                </span>
                                                            </motion.div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Right: Profile & Stats */}
                    <div className="lg:col-span-4 bg-slate-50/60 backdrop-blur-sm relative overflow-hidden border-t lg:border-t-0 lg:border-l border-slate-200">
                        <div className="p-12 h-full flex flex-col justify-between sticky top-0">

                            {/* Stats */}
                            <div className="space-y-8 mb-12">
                                <div>
                                    <div className="text-5xl lg:text-6xl font-bold text-slate-900 tracking-tighter">
                                        <AnimatedNumber value={50} suffix="+" />
                                    </div>
                                    <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-2 border-t border-slate-200 pt-2 inline-block">Brands Optimized</div>
                                </div>
                                <div>
                                    <div className="text-5xl lg:text-6xl font-bold text-slate-900 tracking-tighter">
                                        <AnimatedNumber value={5} suffix="+" />
                                    </div>
                                    <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-2 border-t border-slate-200 pt-2 inline-block">Years Experience</div>
                                </div>
                                <div>
                                    <div className="text-5xl lg:text-6xl font-bold text-slate-900 tracking-tighter">
                                        <AnimatedNumber value={92} suffix="%" />
                                    </div>
                                    <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-2 border-t border-slate-200 pt-2 inline-block">Success Rate</div>
                                </div>
                            </div>

                            {/* Profile */}
                            <div className="space-y-6">
                                <div className="relative aspect-square w-full overflow-hidden rounded-2xl shadow-2xl hover:shadow-indigo-500/20 transition-all duration-500">
                                    <AppImage
                                        src="/abdulsohailsamad.jpg"
                                        alt="Abdul Sohail Samad"
                                        className="object-cover w-full h-full transform hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div>
                                    <h4 className="text-2xl font-bold text-slate-900">Abdul Sohail Samad</h4>
                                    <p className="text-indigo-600 font-medium">Founder & Technical Lead</p>
                                </div>
                                <button
                                    onClick={onBookConsultation}
                                    className="w-full py-4 border-2 border-slate-900 text-slate-900 font-bold hover:bg-slate-900 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm cursor-pointer"
                                >
                                    Book Strategy Call
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ExpertiseShowcase;